<template>
    <b-modal ref="modalEmailStatement" title-tag="h2" :title="modalTitle" lazy>
        <div ref="statementFields">
            <b-form-group
                label-for="period"
                label="Statement Period:"
                label-cols="4"
            >
                <div>
                    <select
                        v-model="selectedPeriod"
                        class="form-control"
                        name="period"
                    >
                        <option :value="0"
                            >All dates - The entire history for the
                            client</option
                        >
                        <option :value="1"
                            >Custom - The date range selected by the
                            clinic</option
                        >
                        <option :value="2"
                            >This week - The current week to date</option
                        >
                        <option :value="3"
                            >This month - The current calendar month</option
                        >
                        <option :value="4"
                            >This quarter - The current calendar quarter</option
                        >
                        <option :value="5"
                            >This year - The current calendar year</option
                        >
                        <option :value="6"
                            >Last week - The last calendar week</option
                        >
                        <option :value="7"
                            >Last month - The last calendar month</option
                        >
                        <option :value="8"
                            >Last quarter - The last calendar quarter</option
                        >
                        <option :value="9"
                            >Last year - The last calendar year</option
                        >
                    </select>
                </div>
                <div class="d-flex pt-2">
                    <date-picker
                        :config="datePickerOptions"
                        v-model="selectedDateStart"
                        v-validate="'required'"
                        class="form-control"
                        placeholder="Start Date (required)"
                        :class="{ 'border-danger': errors.has('dateStart') }"
                        name="dateStart"
                        :disabled="selectedPeriod == 0"
                    ></date-picker>

                    <div class="p-1 ml-1 mlr-1">-</div>
                    <date-picker
                        :config="datePickerOptions"
                        v-model="selectedDateEnd"
                        v-validate="'required'"
                        class="form-control"
                        placeholder="End Date (required)"
                        :class="{ 'border-danger': errors.has('dateEnd') }"
                        name="dateEnd"
                        :disabled="selectedPeriod == 0"
                    ></date-picker>
                </div>
            </b-form-group>
            <b-form-group
                label-for="transaction-detail"
                label="Transaction Detail:"
                label-cols="4"
            >
                <select
                    v-model="transactionDetail"
                    name="transaction-detail"
                    class="form-control"
                >
                    <option :value="1">Include transaction detail</option>
                    <option :value="0"
                        >Do not include transaction detail</option
                    >
                </select>
            </b-form-group>
            <b-form-group label-for="invoice" label="Invoices:" label-cols="4">
                <select
                    v-model="includeInvoice"
                    name="invoice"
                    class="form-control"
                >
                    <option :value="0"
                        >Do not include supporting invoices</option
                    >
                    <option :value="2"
                        >Include unpaid supporting invoices</option
                    >
                    <option :value="1">Include all supporting invoices</option>
                </select>
            </b-form-group>

            <div v-if="isEmail">
                <b-form-group label-for="to" label="To:" label-cols="4">
                    <recepient-email
                        v-model="selectedReceivers"
                        :client="client"
                        @input="
                            value => handleRecipientChange(value, editableDivId)
                        "
                        name="to"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('to') }"
                    ></recepient-email>
                </b-form-group>

                <b-form-group
                    label-for="subject"
                    label="Subject:"
                    label-cols="4"
                >
                    <b-form-input
                        id="subject"
                        v-model="subject"
                        placeholder="Subject (optional)"
                        name="subject"
                    ></b-form-input>
                </b-form-group>

                <b-form-group
                    label-for="message"
                    label="Message:"
                    label-cols="4"
                >
                    <editable
                        :id="editableDivId"
                        ref="editable"
                        :value.sync="message"
                    ></editable>
                </b-form-group>

                <b-form-group
                    label-for="pay_now"
                    label="Pay Online:"
                    label-cols="4"
                    v-if="showPayNow"
                >
                    <b-form-checkbox
                        id="pay_now"
                        v-model="payNow"
                        name="pay_now"
                        plain
                        class="col-form-label"
                    >
                        <span class="fw-normal"
                            >Allow client to pay online</span
                        >
                    </b-form-checkbox>
                </b-form-group>
            </div>
        </div>
        <template v-slot:modal-footer>
            <modal-footer
                :is-loading="isLoading"
                :ok-title="okTitle"
                @ok-action="send"
                @cancel-action="close"
            />
        </template>
    </b-modal>
</template>
<script type="text/javascript">
import EXTENSIONS from "@/constants/extensions";
import Email from "@/services/email";
import CEMAIL from "@/constants/email";
import EmailMixins from "@/mixins/email";
import Billings from "@/services/client-billings";

export default {
    components: {
        recepientEmail: () =>
            import("@/components/commons/recipients-email.vue"),
        modalFooter: () => import("@/components/elements/modal-footer"),
        editable: () => import("@/components/elements/editable")
    },
    mixins: [EmailMixins],
    props: {
        client: {
            default: null,
            required: true
        },
        isEmail: {
            default: true
        }
    },
    data() {
        return {
            waive: 0.0,
            notes: null,

            selectedPeriod: 3,
            datePickerOptions: {
                useCurrent: true,
                format: "DD/MM/YYYY"
            },
            selectedDateStart: null,
            selectedDateEnd: null,
            transactionDetail: 1,
            includeInvoice: 0,
            selectedReceivers: [],
            subject: null,
            message: null,
            receiverNames: null,
            isLoading: false,
            payNow: true
        };
    },
    computed: {
        editableDivId() {
            return "email-message";
        },
        modalTitle() {
            return this.isEmail ? "Email Statement" : "Print Statement";
        },
        okTitle() {
            return this.isEmail ? "Email" : "Print";
        },
        disableSubmit() {
            return this.errors.items.length > 0;
        },
        showPayNow() {
            let balance = this.$store.state.client.client_details
                .account_balance;
            let checkExtensions =
                (this._isExtensionEnabled(EXTENSIONS.CODE.CODE_CCONNECT) ||
                    this._isExtensionEnabled(EXTENSIONS.CODE.CODE_GRAVITY) ||
                    this._isExtensionEnabled(EXTENSIONS.CODE.CODE_STRIPE)) &&
                balance > 0;
            return checkExtensions;
        },
        currentActor() {
            return this.$root.currentActor;
        },
        settings() {
            return this.$root.setting;
        }
    },
    watch: {
        selectedPeriod(newValue) {
            let start = null;
            let end = (this.selectedDateEnd = this._now().toFormat(
                this._dateFormat
            ));
            switch (newValue) {
                case 0:
                    $(".custom-dates").attr("disabled", "disabled");
                    start = this._now()
                        .startOf("month")
                        .toFormat(this._dateFormat);
                    break;
                case 2:
                    start = this._now()
                        .startOf("week")
                        .toFormat(this._dateFormat);
                    break;
                case 3:
                    start = this._now()
                        .startOf("month")
                        .toFormat(this._dateFormat);
                    break;
                case 4:
                    start = this._now()
                        .startOf("quarter")
                        .toFormat(this._dateFormat);
                    break;
                case 5:
                    start = this._now()
                        .startOf("year")
                        .toFormat(this._dateFormat);
                    break;
                case 6:
                    start = this._now()
                        .plus({ days: -7 })
                        .startOf("week")
                        .toFormat(this._dateFormat);
                    end = this._now()
                        .plus({ days: -7 })
                        .endOf("week")
                        .toFormat(this._dateFormat);
                    break;
                case 7:
                    start = this._now()
                        .plus({ months: -1 })
                        .startOf("month")
                        .toFormat(this._dateFormat);
                    end = this._now()
                        .plus({ months: -1 })
                        .endOf("month")
                        .toFormat(this._dateFormat);
                    break;
                case 8:
                    start = this._now()
                        .plus({ quarters: -1 })
                        .startOf("quarter")
                        .toFormat(this._dateFormat);
                    end = this._now()
                        .plus({ quarters: -1 })
                        .endOf("quarter")
                        .toFormat(this._dateFormat);
                    break;
                case 9:
                    start = this._now()
                        .plus({ years: -1 })
                        .startOf("year")
                        .toFormat(this._dateFormat);
                    end = this._now()
                        .plus({ years: -1 })
                        .endOf("year")
                        .toFormat(this._dateFormat);
                    break;
                default:
                    break;
            }

            this.selectedDateStart = start;
            this.selectedDateEnd = end;
        }
    },
    methods: {
        open() {
            this.$refs.modalEmailStatement.show();
            this.setDates();

            this.isEmail ? this.getEmailTemplate() : null;
            this.$validator.reset(); // reset error bag - prevent default red border when open dialog
        },
        setDates() {
            this.datePickerOptions.format = this.getDateFormat();
            this.selectedDateStart = this._now()
                .startOf("month")
                .toFormat(this._dateFormat);
            this.selectedDateEnd = this._now().toFormat(this._dateFormat);
        },
        getEmailTemplate() {
            let params = {
                business_id: this.settings.business.id,
                user_id: this.currentActor.id,
                email_type: CEMAIL.TYPE.STATEMENT,
                client_id: this.client.id
            };

            Email.renderEmailMessage(params).then(response => {
                this.setDetails(response.resources.message);
            });
        },
        setDetails(defaults) {
            let self = this;

            this.subject = defaults.subject;
            this.message = self.setNamesElement(defaults.message);

            setTimeout(function () {
                self.handleRecipientChange(
                    self.selectedReceivers,
                    self.editableDivId
                );
            }, 300);
        },
        async send() {
            let valid = await this.$validator.validateAll();

            if (this.isEmail) {
                if (!valid || this.selectedReceivers.length < 1) {
                    return false;
                }
                this.sendEmail();
            } else {
                if (!valid) {
                    return false;
                }
                this.getDate();
            }
        },
        sendEmail() {
            this.isLoading = true;

            let formData = new FormData();

            let params = {
                fm_report_actor_id: this.client.id,
                fm_report_date_range: this.selectedPeriod,
                fm_report_custom_date_start: this.selectedDateStart,
                fm_report_custom_date_end: this.selectedDateEnd,
                fm_report_include_invoice: this.includeInvoice,
                fm_report_include_transaction: this.transactionDetail,
                fm_report_subject: this.subject,
                fm_report_message: this.message
            };

            if (this.showPayNow) {
                if (this.payNow) {
                    params.pay_now = true;
                }
            }

            _.forEach(params, function (value, key) {
                formData.append(key, value);
            });

            if (this.selectedReceivers.length > 0) {
                _.forEach(this.selectedReceivers, function (element) {
                    formData.append("fm_report_recipient[]", element.to_id);
                });
            } else {
                return null;
            }

            Billings.emailStatement(formData)
                .then(response => {
                    if (response.state == 1) {
                        this._message(response.message, "danger");
                    } else {
                        this._message(response.message);
                    }
                    this.close();
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to email statement",
                        "danger"
                    );
                    this.close();
                    console.error(error);
                });
        },
        getDate() {
            this.isLoading = true;

            let params = {
                fm_report_custom_date_start: this.selectedDateStart,
                fm_report_custom_date_end: this.selectedDateEnd
            };

            Billings.getStatementDate(params)
                .then(response => {
                    this.printStatement(response);
                })
                .then(() => {
                    this.close();
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to process print pdf",
                        "danger"
                    );
                    console.error(error);
                    this.close();
                });
        },
        printStatement(dates) {
            let allHistory = this.selectedPeriod == 0 ? 1 : 0;

            let url =
                process.env.CI_APPS +
                "client/statement/pdf/" +
                this.client.id +
                "?all_history=" +
                allHistory +
                "&from=" +
                dates.from +
                "&to=" +
                dates.to +
                "&include_invoice=" +
                this.includeInvoice +
                "&include_transaction=" +
                this.transactionDetail;

            setTimeout(function () {
                window.open(url, "_blank");
            }, 500);
        },
        close() {
            this.isLoading = false;
            this.$refs.modalEmailStatement.hide();
        }
    }
};
</script>
